import { useState, useEffect } from 'react'
import { colorDeEstatusProveedor } from '@/shared/lib/coloresDeEstatus'
import { useNavigate } from 'react-router-dom'
import { Search, Link as LinkIcon, Plus, Eye, Trash2, PencilLine, CalendarCheck } from 'lucide-react'
import FilterSelect, { type FilterOption } from '@/shared/components/FilterSelect'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import Toast from '@/shared/components/Toast'
import { apiService } from '@/services/api'
import { useAuth } from '@/shared/hooks/useAuth'
import { userHasPermission } from '@/shared/lib/roleUtils'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { formatDate } from '@/shared/lib/dateUtils'
import { getSupplierStatusConfig } from '@/features/proveedores/lib/supplierStatusConfig'
import { Badge } from '@/shared/components/Badge'
import type { ColorDeBadge } from '@/shared/components/Badge'
import { paqueteMensualService } from '@/features/proveedores/api/paqueteMensual.service'
import { EsqueletoTabla } from '@/shared/components/Esqueleto'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface Proveedor {
  id: string
  legalName: string
  commercialName: string | null
  rfc: string
  status: string
  createdAt: string
  account: {
    email: string
    isActive: boolean
  }
}

interface ProveedoresResponse {
  data: Proveedor[]
  meta: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

// Fila de la vista Compras "con documentos vencidos". La provee J-02 / SP-226
// vía GET /internal/suppliers/expired-documents.
interface ExpiredDocsRow {
  supplierId: string
  legalName: string
  rfc: string
  expiredCount: number
}

// Pastilla de la columna Estado documentos.
const DOCUMENTOS_COLOR: Record<'vencidos' | 'vigentes' | 'pendiente' | 'sin_dato', ColorDeBadge> = {
  vencidos: 'rojo',
  vigentes: 'verde',
  pendiente: 'gris',
  sin_dato: 'gris',
}

const DocumentosBadge = ({
  estado,
  vencidos,
}: {
  estado: 'vencidos' | 'vigentes' | 'pendiente' | 'sin_dato'
  vencidos: number
}) => (
  <Badge color={DOCUMENTOS_COLOR[estado]}>
    {estado === 'vencidos'
      ? `Vencidos (${vencidos})`
      : estado === 'vigentes'
        ? 'Vigentes'
        : estado === 'pendiente'
          ? 'Pendiente'
          : 'Sin dato'}
  </Badge>
)

const SuperAdminProveedores = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectSupplierId, setRejectSupplierId] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [proveedores, setProveedores] = useState<Proveedor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expiredRows, setExpiredRows] = useState<ExpiredDocsRow[]>([])
  // Cuantos paquetes mensuales esperan revision. Va en el boton porque al
  // enviar su paquete el proveedor no genera notificacion a Contraloria.
  const [paquetesPorRevisar, setPaquetesPorRevisar] = useState(0)
  // No se pudo consultar el vencimiento de documentos: la columna lo dice en vez
  // de afirmar que estan vigentes.
  const [expiredError, setExpiredError] = useState(false)
  const [filterProceso, setFilterProceso] = useState('')
  const [filterDocumentos, setFilterDocumentos] = useState('')
  const [filterDesde, setFilterDesde] = useState('')
  const [filterHasta, setFilterHasta] = useState('')

  const fetchProveedores = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.request<ProveedoresResponse>(
        '/suppliers'
      )
      // La respuesta puede ser directamente un array o tener una propiedad data
      const data = Array.isArray(response) ? response : (response.data || [])
      setProveedores(data)
      fetchExpiredDocs()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      // console.error('Error al cargar proveedores:', err) // Log ocultado
      setError(err.message || 'Error al cargar los proveedores')
    } finally {
      setLoading(false)
    }
  }

  const fetchExpiredDocs = async () => {
    try {
      // Lo calcula J-02 / SP-226 contra la fecha de vencimiento del expediente.
      const rows = await apiService.request<ExpiredDocsRow[]>(
        '/internal/suppliers/expired-documents'
      )
      setExpiredRows(Array.isArray(rows) ? rows : [])
      setExpiredError(false)
    } catch {
      // Si la consulta falla no se inventa nada: dejar a los demas proveedores
      // como Vigentes seria afirmar que sus documentos estan al dia sin saberlo.
      setExpiredRows([])
      setExpiredError(true)
    }
  }

  useEffect(() => {
    fetchProveedores()
  }, [])

  useEffect(() => {
    paqueteMensualService
      .pendientesDeRevision()
      .then((lista) => setPaquetesPorRevisar(lista.length))
      .catch(() => setPaquetesPorRevisar(0))
  }, [])

  const handleEdit = (supplierId: string) => {
    navigate(`/super-admin-proveedores/${supplierId}/editar`)
  }

  const handleReject = (supplierId: string) => {
    setRejectSupplierId(supplierId)
    setShowRejectModal(true)
  }

  const confirmReject = async () => {
    if (!rejectSupplierId) return

    try {
      setActionLoading(true)
      await apiService.request(
        `/internal/suppliers/${rejectSupplierId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: 'rejected' })
        }
      )
      setToast({ message: 'Proveedor rechazado exitosamente', type: 'success' })
      setShowRejectModal(false)
      setRejectSupplierId(null)
      fetchProveedores() // Refrescar lista
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setToast({ message: err.message || 'Error al rechazar el proveedor', type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  // Cuantos documentos vencidos tiene cada proveedor, por id.
  const vencidosPorProveedor = new Map(
    expiredRows.map((r) => [r.supplierId, r.expiredCount])
  )

  // Estado de documentos que se pinta en la columna. Un proveedor aprobado ya
  // paso la validacion de su expediente, asi que si no tiene vencidos esta
  // vigente; los que no estan aprobados siguen con documentos por entregar.
  type EstadoDocumentos = 'vencidos' | 'vigentes' | 'pendiente' | 'sin_dato'
  const estadoDocumentos = (p: Proveedor): EstadoDocumentos => {
    if ((vencidosPorProveedor.get(p.id) ?? 0) > 0) return 'vencidos'
    if (p.status !== 'approved') return 'pendiente'
    // Aprobado sin vencidos es Vigente solo si de verdad se pudo consultar.
    return expiredError ? 'sin_dato' : 'vigentes'
  }

  const procesoOptions: FilterOption[] = Array.from(
    new Set(proveedores.map((p) => p.status))
  ).map((status) => ({
    value: status,
    label: getSupplierStatusConfig(status).label,
  }))

  const documentosOptions: FilterOption[] = [
    { value: 'vencidos', label: 'Vencidos' },
    { value: 'vigentes', label: 'Vigentes' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'sin_dato', label: 'Sin dato' },
  ]

  const hayFiltros =
    !!searchTerm || !!filterProceso || !!filterDocumentos || !!filterDesde || !!filterHasta

  const limpiarFiltros = () => {
    setSearchTerm('')
    setFilterProceso('')
    setFilterDocumentos('')
    setFilterDesde('')
    setFilterHasta('')
  }

  const filteredProveedores = proveedores.filter((proveedor) => {
    if (proveedor.status !== 'approved') return false

    const q = searchTerm.trim().toLowerCase()
    const coincideBusqueda =
      !q ||
      (proveedor.legalName?.toLowerCase().includes(q) ?? false) ||
      (proveedor.account?.email?.toLowerCase().includes(q) ?? false)

    const creado = proveedor.createdAt?.slice(0, 10) ?? ''

    return (
      coincideBusqueda &&
      (!filterProceso || proveedor.status === filterProceso) &&
      (!filterDocumentos || estadoDocumentos(proveedor) === filterDocumentos) &&
      (!filterDesde || creado >= filterDesde) &&
      (!filterHasta || creado <= filterHasta)
    )
  })

  if (
    userHasPermission(
      'proveedores.administracion.proveedores.consultar',
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    )
  }



  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header: título y acciones en la misma fila */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Gestión Proveedores</h1>
            <p className="text-[#666666]">Administra aquí tu lista de usuarios</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/super-admin-proveedores/paquetes-mensuales')}
              className={estiloDeBoton('secundario')}
            >
              <CalendarCheck className="w-4 h-4" />
              Paquetes mensuales
              {paquetesPorRevisar > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#2563EB] text-white text-xs font-semibold">
                  {paquetesPorRevisar}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/super-admin-proveedores/invitacion/nueva')}
              className={estiloDeBoton('primario')}
            >
              <LinkIcon className="w-4 h-4" />
              Crear invitación
            </button>
            <button
              onClick={() => navigate('/super-admin-proveedores/nuevo')}
              className={estiloDeBoton('primario')}
            >
              <Plus className="w-4 h-4" />
              Nuevo proveedor
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
          <div className="relative w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar en gestión de proveedores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
            />
          </div>

          <FilterSelect
            label="Estado proceso"
            allLabel="Todos los estados"
            options={procesoOptions}
            value={filterProceso}
            onChange={setFilterProceso}
          />

          <FilterSelect
            label="Estado documentos"
            allLabel="Todos los documentos"
            options={documentosOptions}
            value={filterDocumentos}
            onChange={setFilterDocumentos}
          />

          {/* Rango por fecha de creación */}
          <input
            type="date"
            value={filterDesde}
            max={filterHasta || undefined}
            onChange={(e) => setFilterDesde(e.target.value)}
            title="Creado desde"
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: 'light' }}
          />
          <input
            type="date"
            value={filterHasta}
            min={filterDesde || undefined}
            onChange={(e) => setFilterHasta(e.target.value)}
            title="Creado hasta"
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: 'light' }}
          />

          {hayFiltros && (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="text-sm font-semibold text-[#315FB9] hover:underline ml-auto"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          // A-01: silueta de la tabla de proveedores.
          <div className="bg-white rounded-lg border border-[#E0E0E0] overflow-hidden">
            <EsqueletoTabla filas={7} columnas={6} />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <strong>Error:</strong> {error}
            </p>
            <button
              onClick={fetchProveedores}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Proveedor</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estado proceso</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Creado</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estado documentos</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {filteredProveedores.map((proveedor) => (
                    <tr key={proveedor.id} className="hover:bg-[#F4F7FB] transition-colors">
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 cursor-pointer"
                        onClick={() => navigate(`/super-admin-proveedores/${proveedor.id}`)}
                      >
                        <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-[#1B2128]">
                                {proveedor.legalName}
                              </div>
                            </div>
                          </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 cursor-pointer"
                        onClick={() => navigate(`/super-admin-proveedores/${proveedor.id}`)}
                      >{proveedor.account?.email || 'N/A'}</td>
                      <td className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/super-admin-proveedores/${proveedor.id}`)}
                      >
                        <Badge color={colorDeEstatusProveedor(proveedor.status)}>
                          {getSupplierStatusConfig(proveedor.status).label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 cursor-pointer"
                        onClick={() => navigate(`/super-admin-proveedores/${proveedor.id}`)}
                      >{formatDate(proveedor.createdAt)}</td>
                      <td className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/super-admin-proveedores/${proveedor.id}`)}
                      >
                        <DocumentosBadge
                          estado={estadoDocumentos(proveedor)}
                          vencidos={vencidosPorProveedor.get(proveedor.id) ?? 0}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-5">
                          <button
                            onClick={() => navigate(`/super-admin-proveedores/${proveedor.id}`)}
                            className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </button>
                          <button
                            onClick={() => handleEdit(proveedor.id)}
                            className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                            title="Editar proveedor"
                          >
                            <PencilLine className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleReject(proveedor.id)}
                            className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Rechazar proveedor"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredProveedores.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No se encontraron proveedores</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de Rechazo */}
        <ConfirmActionModal
          isOpen={showRejectModal}
          title="Rechazar Proveedor"
          message="¿Estás seguro de que deseas rechazar este proveedor? Esta acción cambiará su estado a 'Rechazado'."
          confirmText="Rechazar"
          variante="peligro"
          onConfirm={confirmReject}
          onCancel={() => {
            setShowRejectModal(false)
            setRejectSupplierId(null)
          }}
          isLoading={actionLoading}
        />

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  )
}

export default SuperAdminProveedores
